import { useEffect, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/db/db';
import { useAuth } from '@/auth/store';
import { enqueueAllLocal, syncOnce } from './client';
import { getCursor } from './state';
import { reconcilePendingClaims } from '@/team/locks';
import { cacheDevices } from '@/auth/devices';
import { usePlan } from '@/plan/store';

const POLL_MS = 20_000;
const DEBOUNCE_MS = 800;

/**
 * When to sync (architecture §7): on sign-in (with a first-time upload), when the tab becomes visible, when the
 * network returns, shortly after any local write, and every 20 s while the tab is visible so the live timer follows.
 */
export function useSyncScheduler() {
  const { status, deviceId } = useAuth();
  const pending = useLiveQuery(() => db.outbox.count(), [], 0) ?? 0;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sync is a Personal feature: nothing is pushed or pulled until the server has said the plan is live. Local
  // writes keep queueing in the outbox meanwhile, so a later upgrade uploads everything at once.
  const entitled = usePlan((s) => s.summary?.entitled === true);
  const active = status === 'signedIn' && !!deviceId && entitled;

  const kick = () => { if (active && deviceId) { void syncOnce(deviceId).catch(() => undefined); void reconcilePendingClaims().catch(() => undefined); } };

  // First sign-in: if the server has never seen this account from here, push everything local first.
  useEffect(() => {
    if (!active || !deviceId) return;
    void (async () => { if ((await getCursor()) === 0) await enqueueAllLocal(); kick(); await cacheDevices().catch(() => undefined); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per sign-in
  }, [active, deviceId]);

  useEffect(() => {
    if (!active) return;
    const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
    window.addEventListener('online', kick); document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(() => { if (document.visibilityState === 'visible') kick(); }, POLL_MS);
    return () => { window.removeEventListener('online', kick); document.removeEventListener('visibilitychange', onVisible); clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- kick closes over stable ids
  }, [active, deviceId]);

  // Debounced push after local writes.
  useEffect(() => {
    if (!active || pending === 0) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(kick, DEBOUNCE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- kick closes over stable ids
  }, [pending, active, deviceId]);

  return { pending };
}
